/**
 * WorkflowContainer
 *
 * Main container for the Job Workflow Wizard.
 * Features a 3-panel layout:
 * - Left: Step navigation sidebar with progress
 * - Center: Main step content area
 * - Right: Context & help panel
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useJobWorkflow, type UseJobWorkflowOptions } from '@/hooks/useJobWorkflow';
import { WORKFLOW_STEPS, type WorkflowStepId } from '@/types/workflow';
import { WorkflowSidebar } from './WorkflowSidebar';
import { WorkflowHelp } from './WorkflowHelp';
import { WorkflowAdvanced } from './WorkflowAdvanced';
import { WorkflowStepBasics } from './steps/WorkflowStepBasics';
import { WorkflowStepScope } from './steps/WorkflowStepScope';
import { WorkflowStepFields } from './steps/WorkflowStepFields';
import { WorkflowStepDocuments } from './steps/WorkflowStepDocuments';
import { WorkflowStepCompliance } from './steps/WorkflowStepCompliance';
import { WorkflowStepInspections } from './steps/WorkflowStepInspections';
import { WorkflowStepReview } from './steps/WorkflowStepReview';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Save,
  X,
  ArrowLeft,
  ArrowRight,
  Loader2,
  RefreshCw,
  ExternalLink,
  Menu,
  HelpCircle,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getWorkflowStep } from '@/types/workflow';

export interface WorkflowContainerProps {
  jobId: string;
  contractorId: string;
  jobData: JobData;
  onExit?: () => void;
  onJobUpdate?: (updates: Partial<JobData>) => void;
  onRerunCompliance?: () => void;
  // Deep link support - navigate to specific step/substep on initial load
  initialStepId?: WorkflowStepId;
  initialSubstepId?: string;
}

// Job data interface (subset of full Job type needed for workflow)
export interface JobData {
  id: string;
  title: string;
  description?: string;
  location?: string;
  jurisdictionId?: number | null;
  jurisdictionCode?: string | null;
  jurisdictionAhjName?: string | null;
  jurisdictionOverlays?: Record<string, boolean> | null;
  permitType?: string | null;
  jobScope?: string | null;
  trade?: string;
  estimatedValue?: number;
  propertyType?: string;
  contractorRole?: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  // Add other fields as needed
  [key: string]: unknown;
}

export function WorkflowContainer({
  jobId,
  contractorId,
  jobData,
  onExit,
  onJobUpdate,
  onRerunCompliance,
  initialStepId,
  initialSubstepId,
}: WorkflowContainerProps) {
  const navigate = useNavigate();
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevStepRef = useRef<WorkflowStepId | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const hasInitialNavigated = useRef(false);

  // Workflow state management
  const workflow = useJobWorkflow({
    jobId,
    contractorId,
    autoLoad: true,
  });

  // Deep link: navigate to initial step/substep when workflow loads
  useEffect(() => {
    if (
      !workflow.isLoading &&
      workflow.state &&
      initialStepId &&
      !hasInitialNavigated.current
    ) {
      hasInitialNavigated.current = true;
      workflow.goToStep(initialStepId, initialSubstepId);
    }
  }, [workflow.isLoading, workflow.state, initialStepId, initialSubstepId, workflow.goToStep]);

  // Track step changes for animation
  useEffect(() => {
    if (prevStepRef.current && prevStepRef.current !== workflow.currentStepId) {
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 300);
      return () => clearTimeout(timer);
    }
    prevStepRef.current = workflow.currentStepId;
  }, [workflow.currentStepId]);

  // Handle exit - show confirmation if unsaved changes
  // With wizard-first routing, exit goes to jobs list (not job detail)
  const handleExit = useCallback(() => {
    if (workflow.hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      if (onExit) {
        onExit();
      } else {
        navigate('/jobs');
      }
    }
  }, [workflow.hasUnsavedChanges, onExit, navigate]);

  // Save and exit
  const handleSaveAndExit = useCallback(async () => {
    await workflow.save();
    setShowExitDialog(false);
    if (onExit) {
      onExit();
    } else {
      navigate('/jobs');
    }
  }, [workflow, onExit, navigate]);

  // Exit without saving
  const handleExitWithoutSaving = useCallback(() => {
    setShowExitDialog(false);
    if (onExit) {
      onExit();
    } else {
      navigate('/jobs');
    }
  }, [onExit, navigate]);

  // Navigate to job details page (secondary reference view)
  // Pass fromWorkflow=true to hide redundant tabs (Documents, Photos, Inspections)
  const handleViewDetails = useCallback(() => {
    navigate(`/jobs/${jobId}/details?fromWorkflow=true`);
  }, [navigate, jobId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to exit
      if (e.key === 'Escape' && !showExitDialog) {
        handleExit();
      }
      // Ctrl/Cmd + S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        workflow.save();
      }
      // Ctrl/Cmd + Enter to continue
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && workflow.canGoNext) {
        e.preventDefault();
        workflow.goNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleExit, workflow, showExitDialog]);

  // Render current step content
  const renderStepContent = () => {
    switch (workflow.currentStepId) {
      case 'basics':
        return (
          <WorkflowStepBasics
            jobData={jobData}
            workflow={workflow}
            onUpdate={onJobUpdate}
          />
        );
      case 'scope':
        return (
          <WorkflowStepScope
            jobData={jobData}
            workflow={workflow}
            onUpdate={onJobUpdate}
          />
        );
      case 'fields':
        return (
          <WorkflowStepFields
            jobData={jobData}
            workflow={workflow}
            onUpdate={onJobUpdate}
          />
        );
      case 'documents':
        return (
          <WorkflowStepDocuments
            jobId={jobId}
            contractorId={contractorId}
            jobData={jobData}
            workflow={workflow}
          />
        );
      case 'compliance':
        return (
          <WorkflowStepCompliance
            jobId={jobId}
            contractorId={contractorId}
            jobData={jobData}
            workflow={workflow}
            onRerunCheck={onRerunCompliance}
          />
        );
      case 'inspections':
        return (
          <WorkflowStepInspections
            jobId={jobId}
            jobData={jobData}
            workflow={workflow}
          />
        );
      case 'review':
        return (
          <WorkflowStepReview
            jobId={jobId}
            contractorId={contractorId}
            jobData={jobData}
            workflow={workflow}
          />
        );
      default:
        return null;
    }
  };

  // Get current step config
  const currentStepConfig = getWorkflowStep(workflow.currentStepId);

  // Loading state
  if (workflow.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading workflow...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (workflow.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <p className="text-destructive">{workflow.error}</p>
          <Button onClick={() => navigate('/jobs')}>
            Return to Jobs
          </Button>
        </div>
      </div>
    );
  }

  // Calculate overall progress
  const overallProgress = Math.round(
    (workflow.progress.dataCompletenessPct +
      workflow.progress.packetCompletenessPct +
      workflow.progress.complianceReadinessPct) /
      3
  );

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Header */}
      <header className="border-b bg-card px-3 sm:px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Mobile Step Navigation Trigger */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden h-8 w-8 p-0"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 p-0">
              <SheetHeader className="p-4 border-b">
                <SheetTitle>Workflow Steps</SheetTitle>
              </SheetHeader>
              <div className="p-0">
                <WorkflowSidebar
                  currentStepId={workflow.currentStepId}
                  stepStatus={workflow.state?.stepStatus || {}}
                  progress={workflow.progress}
                  onStepClick={(stepId) => {
                    workflow.goToStep(stepId);
                    setMobileMenuOpen(false);
                  }}
                  blockerCount={workflow.state?.blockers.length || 0}
                  warningCount={workflow.state?.warnings.length || 0}
                  className="border-0 w-full"
                />
              </div>
            </SheetContent>
          </Sheet>

          <h1 className="text-base sm:text-lg font-semibold truncate max-w-[150px] sm:max-w-[300px]">
            {jobData.title || `Job ${jobId}`}
          </h1>

          {/* Mobile Step Indicator */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="flex md:hidden items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full"
          >
            <span>{workflow.currentStepNumber}/{workflow.totalSteps}</span>
            <ChevronDown className="h-3 w-3" />
          </button>

          {/* Desktop Progress */}
          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              Step {workflow.currentStepNumber} of {workflow.totalSteps}
            </span>
            <Progress
              value={overallProgress}
              className={cn(
                "w-24 h-2 [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out",
                overallProgress === 100 && "[&>div]:bg-green-500"
              )}
            />
            <span className={cn(
              "text-xs transition-colors duration-300",
              overallProgress === 100 && "text-green-600 font-medium"
            )}>
              {overallProgress}%
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Mobile Help Trigger */}
          <Sheet open={mobileHelpOpen} onOpenChange={setMobileHelpOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden h-8 w-8 p-0"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-96 p-0">
              <SheetHeader className="p-4 border-b">
                <SheetTitle>Help & Context</SheetTitle>
              </SheetHeader>
              <div className="p-4 overflow-y-auto max-h-[calc(100vh-80px)]">
                <WorkflowHelp
                  currentStepId={workflow.currentStepId}
                  currentSubstepId={workflow.currentSubstepId}
                  jobData={jobData}
                  issues={workflow.getIssuesForCurrentStep()}
                  isMobile
                />
              </div>
            </SheetContent>
          </Sheet>
          {/* View Details button - links to secondary reference page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewDetails}
            title="View job details"
          >
            <ExternalLink className="h-4 w-4" />
            <span className="ml-2 hidden sm:inline">Details</span>
          </Button>
          {/* Advanced drawer for power users */}
          <WorkflowAdvanced
            jobId={jobId}
            contractorId={contractorId}
            jobData={jobData}
            workflowState={workflow.state}
            onNavigateToJobDetail={() => navigate(`/jobs/${jobId}/details?fromWorkflow=true`)}
          />
          {onRerunCompliance && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRerunCompliance}
              title="Re-run compliance check"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="ml-2 hidden sm:inline">Re-check</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => workflow.save()}
            disabled={workflow.isSaving}
          >
            {workflow.isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span className="ml-2 hidden sm:inline">Save</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExit}>
            <X className="h-4 w-4" />
            <span className="ml-2 hidden sm:inline">Exit</span>
          </Button>
        </div>
      </header>

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left Sidebar - Step Navigation */}
        <WorkflowSidebar
          currentStepId={workflow.currentStepId}
          stepStatus={workflow.state?.stepStatus || {}}
          progress={workflow.progress}
          onStepClick={(stepId) => workflow.goToStep(stepId)}
          blockerCount={workflow.state?.blockers.length || 0}
          warningCount={workflow.state?.warnings.length || 0}
        />

        {/* Center - Main Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0 isolate">
          <div className="max-w-3xl mx-auto">
            {/* Step Title */}
            {currentStepConfig && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold">{currentStepConfig.title}</h2>
                <p className="text-muted-foreground mt-1">
                  {currentStepConfig.description}
                </p>
              </div>
            )}

            {/* Step Content with transition animation */}
            <div
              className={cn(
                "space-y-6 transition-all duration-300 ease-out",
                isTransitioning
                  ? "opacity-0 translate-y-2"
                  : "opacity-100 translate-y-0"
              )}
            >
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={workflow.goPrev}
                disabled={!workflow.canGoPrev}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>

              {workflow.currentStepId !== 'review' ? (
                <Button
                  onClick={workflow.goNext}
                  disabled={!workflow.canGoNext}
                  className="w-full sm:w-auto transition-all duration-200 hover:scale-105"
                >
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  disabled={!workflow.isReadyForSubmission}
                  className={cn(
                    "w-full sm:w-auto transition-all duration-300",
                    workflow.isReadyForSubmission &&
                      'bg-green-600 hover:bg-green-700 hover:scale-105 shadow-lg shadow-green-500/25 animate-pulse'
                  )}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ready to Submit
                </Button>
              )}
            </div>
          </div>
        </main>

        {/* Right Panel - Context & Help */}
        <WorkflowHelp
          currentStepId={workflow.currentStepId}
          currentSubstepId={workflow.currentSubstepId}
          jobData={jobData}
          issues={workflow.getIssuesForCurrentStep()}
        />
      </div>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Exit workflow?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save your progress
              before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowExitDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <Button variant="outline" onClick={handleExitWithoutSaving}>
              Exit Without Saving
            </Button>
            <AlertDialogAction onClick={handleSaveAndExit}>
              <Save className="h-4 w-4 mr-2" />
              Save & Exit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
